import { useEffect } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { useACNavigate } from 'academy-common-lib/hooks/useACNavigate';
import { isPreAssessmentCompleted } from '../preAssessmentQuiz/utils/preAssessmentQuizUtils';
import { useTrackRedirectUrl } from '../userProgress/userProgressQueries';
import { getTrackExamPageUrl, getTrackIntroPageUrl, getTrackWrapUpPageUrl } from '../../utils/RoutingUtils.migrated';
import { PRE_ASSESSMENT_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS, TRACK_COMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT } from '../../constants/Navigation.legacy';
import { UserTrackStatuses } from 'academy-common-lib/constants/TracksConstants';
import { PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD } from '../../constants/PreAssessmentsConstants';
import { usePlaylistIdFromSearch } from '../playlistContent/hooks.migrated';
const findNextFocusContentInLesson = (content, startIndex, perContentScores, examId) => {
  for (let i = startIndex; i < content.length; ++i) {
    const {
      id
    } = content[i];
    const cachedScore = perContentScores[id];
    if (id === examId || !cachedScore) {
      continue;
    }
    const score = cachedScore.correct / cachedScore.total;
    if (score <= PRE_ASSESSMENTS_CONTENT_PASS_THRESHOLD) {
      return id;
    }
  }
  return undefined;
};
const getUrlAfterLesson = (track, afterLessonIndex, isInPlaylist, getLessonPreQuizState) => {
  for (let i = afterLessonIndex + 1; i < track.lessons.length; ++i) {
    var _track$exam;
    const nextLesson = track.lessons[i];
    const preQuizState = getLessonPreQuizState === null || getLessonPreQuizState === void 0 ? void 0 : getLessonPreQuizState(nextLesson.id);
    if (!preQuizState || !isPreAssessmentCompleted(preQuizState)) {
      return `/tracks/${track.id}/${nextLesson.id}/${nextLesson.content[0].id}`;
    }
    const focusId = findNextFocusContentInLesson(nextLesson.content, 0, preQuizState.perContentScores, (_track$exam = track.exam) === null || _track$exam === void 0 ? void 0 : _track$exam.id);
    if (focusId) {
      return `/tracks/${track.id}/${nextLesson.id}/${focusId}`;
    }
  }
  if (track.exam && !track.exam.completed) {
    return getTrackExamPageUrl(track.id.toString());
  }
  if (track.certification && !track.exam) {
    return `/tracks/${track.id}/${TRACK_COMPLETE_URL_FRAGMENT}`;
  }
  return isInPlaylist ? `/tracks/${track.id}/${TRACK_COMPLETE_URL_FRAGMENT}` : `/tracks/${track.id}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`;
};
export const getNextContentUrl = ({
  track,
  lesson,
  contentId,
  isInPlaylist = false,
  getLessonPreQuizState
}) => {
  const lessonIndex = track.lessons.findIndex(l => l.id === lesson.id);
  const contentIndex = lesson.content.findIndex(c => c.id === contentId);
  const preQuizState = getLessonPreQuizState === null || getLessonPreQuizState === void 0 ? void 0 : getLessonPreQuizState(lesson.id);
  const hasCompletedPreQuiz = preQuizState && isPreAssessmentCompleted(preQuizState);
  if (hasCompletedPreQuiz) {
    var _track$exam2;
    const nextFocusId = findNextFocusContentInLesson(lesson.content, contentIndex + 1, preQuizState.perContentScores, (_track$exam2 = track.exam) === null || _track$exam2 === void 0 ? void 0 : _track$exam2.id);
    if (nextFocusId) {
      return `/tracks/${track.id}/${lesson.id}/${nextFocusId}`;
    }
    return getUrlAfterLesson(track, lessonIndex, isInPlaylist, getLessonPreQuizState);
  }
  const contentIsLastInLesson = contentIndex >= lesson.content.length - 1;
  if (!contentIsLastInLesson) {
    return `/tracks/${track.id}/${lesson.id}/${lesson.content[contentIndex + 1].id}`;
  }
  return getUrlAfterLesson(track, lessonIndex, isInPlaylist, getLessonPreQuizState);
};
export const getFirstContentForTrackUrl = track => {
  const firstLesson = track.lessons[0];
  const firstLessonContent = firstLesson.content[0];
  return `/tracks/${track.id}/${firstLesson.id}/${firstLessonContent.id}`;
};
export const useTrackRedirection = ({
  trackId,
  language,
  track,
  lesson,
  contentId,
  lessonId,
  trackProgress,
  contentFragment,
  lessonFragment
}) => {
  const {
    trackRedirectUrl: firstUnprogressedContentUrl,
    trackRedirectUrlLoading
  } = useTrackRedirectUrl(trackId);
  const history = useHistory();
  const navigate = useACNavigate();
  const isInPlaylist = !!usePlaylistIdFromSearch();
  useEffect(() => {
    if (!track || !trackProgress || trackRedirectUrlLoading) return;
    const userHasntSetLessonOrContent = !lessonId && !contentId;
    let pathname;
    if (isInPlaylist && lessonFragment === TRACK_RECOMMENDATIONS_URL_FRAGMENT) {
      pathname = `/tracks/${track.id}/${TRACK_COMPLETE_URL_FRAGMENT}`;
    } else if (userHasntSetLessonOrContent && !lessonFragment) {
      var _track$exam3, _track$introVideo;
      if (trackProgress.status === UserTrackStatuses.COMPLETED && (_track$exam3 = track.exam) !== null && _track$exam3 !== void 0 && _track$exam3.id) {
        pathname = getTrackExamPageUrl(track.id.toString());
      } else if (trackProgress.status === UserTrackStatuses.COMPLETED && trackProgress.trackAward && !track.exam) {
        pathname = getTrackWrapUpPageUrl(track.id.toString());
      } else if ((trackProgress.status === UserTrackStatuses.NOT_STARTED || trackProgress.status === UserTrackStatuses.COMPLETED) && (_track$introVideo = track.introVideo) !== null && _track$introVideo !== void 0 && _track$introVideo.id) {
        pathname = getTrackIntroPageUrl(track.id.toString());
      } else if (firstUnprogressedContentUrl) {
        pathname = firstUnprogressedContentUrl;
      } else {
        pathname = getFirstContentForTrackUrl(track);
      }
    } else if (lesson && !(contentId && lesson.content.some(content => content.id === contentId)) && contentFragment !== SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS && contentFragment !== PRE_ASSESSMENT_URL_FRAGMENT) {
      pathname = `/tracks/${track.id}/${lessonId}/${lesson.content[0].id}`;
    }
    if (pathname) {
      navigate(Object.assign({}, history.location, {
        pathname
      }), {
        replace: true
      });
    }
  }, [track, trackProgress, trackRedirectUrlLoading, firstUnprogressedContentUrl, lessonId, contentId, contentFragment, lesson, trackId, language, history, navigate, lessonFragment, isInPlaylist]);
};